import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Plus, X, CheckCircle2, LifeBuoy, PencilLine, ArrowRight } from 'lucide-react';
import TicketForm from '../../components/dashboard/TicketForm';
import TicketList from '../../components/dashboard/TicketList';
import { api } from '../../lib/api';

const NOTICE_KEY = 'nexsav_tickets_support_notice_dismissed';

export default function DashboardTickets() {
  const [tickets, setTickets] = useState([]);
  const [viewer, setViewer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [justSubmitted, setJustSubmitted] = useState(null);
  const [noticeOpen, setNoticeOpen] = useState(() => {
    try { return localStorage.getItem(NOTICE_KEY) !== '1'; } catch { return true; }
  });

  const dismissNotice = () => {
    setNoticeOpen(false);
    try { localStorage.setItem(NOTICE_KEY, '1'); } catch { /* ignore */ }
  };

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const data = await api.get('/api/tickets');
      setTickets(data.tickets || []);
      setViewer(data.viewer || null);
    } catch (err) {
      setLoadError(err?.message || 'Failed to load tickets');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleDelete = useCallback(async (id) => {
    try {
      await api.delete(`/api/tickets/${id}`);
      setTickets((list) => list.filter((t) => t.id !== id));
    } catch (err) {
      setLoadError(err?.message || 'Failed to delete ticket');
    }
  }, []);

  const handleEdit = useCallback(async (id, fields) => {
    try {
      const data = await api.put(`/api/tickets/${id}`, fields);
      setTickets((list) => list.map((t) => (t.id === id ? { ...t, ...data.ticket } : t)));
      return true;
    } catch (err) {
      setLoadError(err?.message || 'Failed to update ticket');
      return false;
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const onCreated = (ticket) => {
    setTickets((prev) => [ticket, ...prev]);
    setFormOpen(false);
    setJustSubmitted(ticket);
    setTimeout(() => setJustSubmitted(null), 5000);
  };

  return (
    <div>
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-white tracking-tight"><span className="serif-acc accent-text">Tickets</span></h1>
          <p className="text-sm text-gray-500 mt-1">
            Report issues with your website. NexSav responds within 24–48 hours.
          </p>
        </div>
        {!formOpen && (
          <button
            type="button"
            onClick={() => setFormOpen(true)}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#B0FC38] text-gray-900 text-sm font-semibold hover:bg-[#9BE22E] shadow-lg shadow-[#B0FC38]/20 transition-all shrink-0"
          >
            <Plus className="w-4 h-4" />
            New ticket
          </button>
        )}
        {formOpen && (
          <button
            type="button"
            onClick={() => setFormOpen(false)}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-white/[0.1] text-sm text-gray-300 hover:text-white hover:border-white/[0.2] transition-all shrink-0"
          >
            <X className="w-4 h-4" />
            Cancel
          </button>
        )}
      </div>

      <AnimatePresence initial={false}>
        {noticeOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="mb-5 flex items-start gap-3 px-4 py-3.5 rounded-xl bg-[#B0FC38]/[0.07] border border-[#B0FC38]/25">
              <div className="w-8 h-8 rounded-lg bg-[#B0FC38]/[0.12] border border-[#B0FC38]/30 flex items-center justify-center shrink-0">
                <LifeBuoy className="w-4 h-4 text-[#B0FC38]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-white">Tickets are for support issues</div>
                <p className="text-[12px] text-gray-400 mt-0.5 leading-relaxed">
                  Use this for something broken, a question, or billing. To change your website's
                  content, like text, images, or links, head to <span className="text-gray-300">Content Edits</span> instead.
                </p>
                <Link
                  to="/portal/edits"
                  className="inline-flex items-center gap-1.5 mt-2 text-[12px] font-medium text-[#B0FC38] hover:text-white transition-colors"
                >
                  <PencilLine className="w-3.5 h-3.5" />
                  Go to Content Edits
                  <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
              <button
                type="button"
                onClick={dismissNotice}
                className="p-1 rounded-lg text-gray-500 hover:text-white hover:bg-white/[0.05] shrink-0"
                aria-label="Dismiss"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {justSubmitted && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
            className="mb-5 flex items-center gap-3 px-4 py-3 rounded-xl bg-[#B0FC38]/[0.08] border border-[#B0FC38]/25"
          >
            <CheckCircle2 className="w-4 h-4 text-[#B0FC38] shrink-0" />
            <div className="text-sm text-gray-200 flex-1">
              Ticket submitted. NexSav has been notified by email.
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence initial={false}>
        {formOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25, ease: [0.25, 0.1, 0.25, 1] }}
            className="overflow-hidden"
          >
            <div className="mb-6">
              <TicketForm onCreated={onCreated} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {loadError && (
        <div className="mb-4 px-4 py-3 rounded-lg bg-red-500/[0.08] border border-red-500/20 text-[13px] text-red-300/90">
          {loadError}
        </div>
      )}

      <TicketList tickets={tickets} loading={loading} onDelete={handleDelete} onEdit={handleEdit} viewer={viewer} />
    </div>
  );
}
